import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Grid,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useSearchParams } from 'react-router-dom';
import { deleteUserAndData, getAllUsers, type AdminUserRow } from '../services/usersApi';
import { useAdminScope } from '../context/AdminScopeContext';
import ConfirmDeleteUserDialog from '../components/users/ConfirmDeleteUserDialog';
import StylePopularityCircles from '../components/users/StylePopularityCircles';
import {
  downloadCsv,
  getUsersAnalytics,
  getUsersExportUrl,
  type StudentMusicStylesAnalytics,
  type UsersAnalytics,
} from '../services/adminAnalyticsApi';
import { useLanguage } from '../hooks/useLanguage';
import { UserStatusBadge, UserRoleBadge, OutlinedBadge, UserActivityBadge, isUserRecentlyActive } from '@tms/shared-ui';
import UserListAvatar from '../components/users/UserListAvatar';
import { ContentPageLayout } from '../components/Shared';

const displayName = (u: AdminUserRow) => {
  const full = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  if (full) return full;
  return u.nickname || u.email?.split('@')[0] || 'User';
};

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const subscriptionChipColor = (state?: string): 'success' | 'warning' | 'default' => {
  if (state === 'paid') return 'success';
  if (state === 'trial' || state === 'grace') return 'warning';
  return 'default';
};

const INACTIVE_DAYS = 30;

type SortKey = 'name' | 'registered' | 'lastActive' | 'subscription';

type InsightFilter = 'active' | 'inactive' | 'newRegistrations' | 'noTeacher' | 'noSubscription';

type MusicStyleCoverageFilter = 'with' | 'none';

function parseMusicStyleTags(searchParams: URLSearchParams): string[] {
  const raw = searchParams.get('musicStyles');
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

function parseMusicStyleCoverage(searchParams: URLSearchParams): MusicStyleCoverageFilter | null {
  const value = searchParams.get('musicStyleCoverage');
  if (value === 'with' || value === 'none') return value;
  return null;
}

function studentMatchesMusicStyleFilters(
  user: AdminUserRow,
  coverage: MusicStyleCoverageFilter | null,
  styleTags: string[],
): boolean {
  if (user.role !== 'Student') return false;
  const styles = user.preferredMusicStyles ?? [];
  if (coverage === 'with' && styles.length === 0) return false;
  if (coverage === 'none' && styles.length > 0) return false;
  if (styleTags.length > 0 && !styleTags.some((tag) => styles.includes(tag))) return false;
  return true;
}

function resolveSelectedInsight(searchParams: URLSearchParams): InsightFilter | null {
  const activity = searchParams.get('activity');
  if (activity === 'active') return 'active';
  if (activity === 'inactive' || searchParams.get('inactive') === '1') return 'inactive';
  if (searchParams.get('registered') === '30d') return 'newRegistrations';
  if (searchParams.get('noTeacher') === '1') return 'noTeacher';
  if (searchParams.get('noSubscription') === '1') return 'noSubscription';
  return null;
}

function isRegisteredWithinDays(createdAt: string | undefined, days: number): boolean {
  if (!createdAt) return false;
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return new Date(createdAt).getTime() >= cutoff;
}

/** Matches backend teacher logic: role Teacher, isTeacher flag, or has assigned students. */
function userActsAsTeacher(u: AdminUserRow): boolean {
  return (
    u.role === 'Teacher' ||
    Boolean(u.isTeacher) ||
    (u.studentIds?.length ?? 0) > 0
  );
}

function matchesRoleFilter(u: AdminUserRow, role: string): boolean {
  if (!role) return true;
  if (role === 'Teacher') return userActsAsTeacher(u);
  return u.role === role;
}

function UserRoleBadges({ user }: { user: AdminUserRow }) {
  return (
    <>
      {user.role ? <UserRoleBadge role={user.role} /> : null}
      {userActsAsTeacher(user) && user.role !== 'Teacher' ? (
        <UserRoleBadge role="Teacher" />
      ) : null}
    </>
  );
}

function deriveRisk(u: AdminUserRow): 'high' | 'medium' | 'low' | 'none' {
  if (u.status === 'Banned') return 'high';
  if (u.role === 'Student' && !u.teacherLabel) return 'medium';
  if (!isUserRecentlyActive(u.lastConnected, INACTIVE_DAYS)) return 'medium';
  if (u.subscriptionState === 'grace') return 'high';
  return 'none';
}

const AnalyticsMetricCard: React.FC<{
  label: string;
  value: number | string;
  hint?: string;
  selected?: boolean;
  onClick?: () => void;
}> = ({ label, value, hint, selected, onClick }) => (
  <Paper
    component={onClick ? 'button' : 'div'}
    type={onClick ? 'button' : undefined}
    onClick={onClick}
    variant="outlined"
    sx={{
      p: 1.5,
      textAlign: 'center',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      width: '100%',
      cursor: onClick ? 'pointer' : 'default',
      font: 'inherit',
      color: 'inherit',
      borderWidth: selected ? 2 : 1,
      borderColor: selected ? 'primary.main' : 'divider',
      bgcolor: selected ? 'action.selected' : 'background.paper',
      transition: 'border-color 0.15s, background-color 0.15s',
      ...(onClick
        ? {
            '&:hover': {
              bgcolor: selected ? 'action.selected' : 'action.hover',
              borderColor: 'primary.main',
            },
          }
        : {}),
    }}
  >
    <Typography variant="h5" color="primary" fontWeight={700} lineHeight={1.2}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
      {label}
    </Typography>
    {hint ? (
      <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.25 }}>
        {hint}
      </Typography>
    ) : null}
    {onClick ? (
      <Typography variant="caption" color={selected ? 'primary.main' : 'text.disabled'} sx={{ mt: 0.5 }}>
        {selected ? 'Showing matching users below' : 'Click to view users'}
      </Typography>
    ) : null}
  </Paper>
);

function UsersAnalyticsSection({
  analytics,
  selectedInsight,
  onInsightClick,
  searchParams,
  onMusicStyleCoverageClick,
  onMusicStyleTagClick,
}: {
  analytics: UsersAnalytics;
  selectedInsight: InsightFilter | null;
  onInsightClick: (insight: InsightFilter) => void;
  searchParams: URLSearchParams;
  onMusicStyleCoverageClick: (coverage: MusicStyleCoverageFilter) => void;
  onMusicStyleTagClick: (tag: string) => void;
}) {
  const musicStyles = analytics.studentMusicStyles;
  const styleCoveragePct =
    musicStyles && musicStyles.totalStudents > 0
      ? Math.round((musicStyles.studentsWithStyles / musicStyles.totalStudents) * 100)
      : 0;
  const styleChartData = musicStyles?.distribution.filter((row) => row.count > 0) ?? [];
  const newRegistrations30d = analytics.registrationTrend.reduce((sum, row) => sum + row.count, 0);

  return (
    <Stack spacing={2} sx={{ mt: 2, mb: 2 }}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
          Activity & student health
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(9.5rem, 1fr))',
            gap: 1.5,
            overflowX: 'auto',
            pb: 0.25,
          }}
        >
          <AnalyticsMetricCard
            label="Active (30d)"
            value={analytics.activeLast30d}
            selected={selectedInsight === 'active'}
            onClick={() => onInsightClick('active')}
          />
          <AnalyticsMetricCard
            label="Inactive (30d)"
            value={analytics.inactiveLast30d}
            selected={selectedInsight === 'inactive'}
            onClick={() => onInsightClick('inactive')}
          />
          <AnalyticsMetricCard
            label="New registrations (30d)"
            value={newRegistrations30d}
            selected={selectedInsight === 'newRegistrations'}
            onClick={() => onInsightClick('newRegistrations')}
          />
          <AnalyticsMetricCard
            label="Students without teacher"
            value={analytics.studentsWithoutTeacher}
            selected={selectedInsight === 'noTeacher'}
            onClick={() => onInsightClick('noTeacher')}
          />
          <AnalyticsMetricCard
            label="Students without subscription"
            value={analytics.studentsWithoutSubscription}
            selected={selectedInsight === 'noSubscription'}
            onClick={() => onInsightClick('noSubscription')}
          />
        </Box>
      </Paper>

      {musicStyles ? (
        <StudentMusicStylesSection
          musicStyles={musicStyles}
          styleCoveragePct={styleCoveragePct}
          styleChartData={styleChartData}
          selectedCoverage={parseMusicStyleCoverage(searchParams)}
          selectedStyleTags={parseMusicStyleTags(searchParams)}
          onCoverageClick={onMusicStyleCoverageClick}
          onStyleTagClick={onMusicStyleTagClick}
        />
      ) : null}
    </Stack>
  );
}

function StudentMusicStylesSection({
  musicStyles,
  styleCoveragePct,
  styleChartData,
  selectedCoverage,
  selectedStyleTags,
  onCoverageClick,
  onStyleTagClick,
}: {
  musicStyles: StudentMusicStylesAnalytics;
  styleCoveragePct: number;
  styleChartData: StudentMusicStylesAnalytics['distribution'];
  selectedCoverage: MusicStyleCoverageFilter | null;
  selectedStyleTags: string[];
  onCoverageClick: (coverage: MusicStyleCoverageFilter) => void;
  onStyleTagClick: (tag: string) => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={0.5}
        mb={2}
      >
        <Typography variant="subtitle1" fontWeight={600} color="primary">
          Student music style preferences
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {musicStyles.totalStudents} students · {styleCoveragePct}% with at least one style
        </Typography>
      </Box>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={3}>
          <Stack spacing={1.5} height="100%">
            <AnalyticsMetricCard
              label="With music styles"
              value={musicStyles.studentsWithStyles}
              hint={`${styleCoveragePct}% of students`}
              selected={selectedCoverage === 'with'}
              onClick={() => onCoverageClick('with')}
            />
            <AnalyticsMetricCard
              label="No styles set"
              value={musicStyles.studentsWithoutStyles}
              hint={
                musicStyles.studentsWithoutStyles > 0
                  ? `${100 - styleCoveragePct}% of students`
                  : undefined
              }
              selected={selectedCoverage === 'none'}
              onClick={() => onCoverageClick('none')}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Style popularity (students can select multiple) · click a style to filter
            </Typography>
            {styleChartData.length > 0 ? (
              <StylePopularityCircles
                data={styleChartData}
                selectedStyleTags={selectedStyleTags}
                onStyleTagClick={onStyleTagClick}
              />
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No style selections recorded yet.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
}

const UsersManagement: React.FC = () => {
  const { navigateWithLang } = useLanguage();
  const { scope, userId: currentUserId } = useAdminScope();
  const canDeleteUsers = scope === 'full';
  const [searchParams, setSearchParams] = useSearchParams();
  const userListRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<AdminUserRow | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<UsersAnalytics | null>(null);
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');
  const [sortKey, setSortKey] = useState<SortKey>('lastActive');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    setQ(searchParams.get('q') || '');
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [usersRes, analyticsRes] = await Promise.all([
          getAllUsers(),
          getUsersAnalytics().catch(() => null),
        ]);
        const list = usersRes.success && usersRes.data?.users ? usersRes.data.users : [];
        if (!cancelled) {
          setUsers(list);
          if (analyticsRes?.data) setAnalytics(analyticsRes.data);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load users');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const roleFilter = searchParams.get('role') || '';
  const statusFilter = searchParams.get('status') || '';
  const teacherFilter = searchParams.get('teacherId') || '';
  const subscriptionFilter = searchParams.get('subscription') || '';
  const activityFilter = searchParams.get('activity') || '';
  const activeFilter = activityFilter === 'active';
  const inactiveFilter = activityFilter === 'inactive' || searchParams.get('inactive') === '1';
  const noTeacherFilter = searchParams.get('noTeacher') === '1';
  const noSubscriptionFilter = searchParams.get('noSubscription') === '1';
  const newRegistrationsFilter = searchParams.get('registered') === '30d';
  const selectedInsight = resolveSelectedInsight(searchParams);
  const musicStyleCoverage = parseMusicStyleCoverage(searchParams);
  const musicStyleTags = parseMusicStyleTags(searchParams);
  const hasMusicStyleFilter = Boolean(musicStyleCoverage) || musicStyleTags.length > 0;

  const filtered = useMemo(() => {
    let list = users;
    if (roleFilter) list = list.filter((u) => matchesRoleFilter(u, roleFilter));
    if (statusFilter) list = list.filter((u) => u.status === statusFilter);
    if (teacherFilter) list = list.filter((u) => String(u.teacherId || '') === teacherFilter);
    if (subscriptionFilter) {
      list = list.filter((u) => (u.subscriptionState || 'none') === subscriptionFilter);
    }
    if (activeFilter) {
      list = list.filter((u) => isUserRecentlyActive(u.lastConnected, INACTIVE_DAYS));
    } else if (inactiveFilter) {
      list = list.filter((u) => !isUserRecentlyActive(u.lastConnected, INACTIVE_DAYS));
    }
    if (noTeacherFilter) {
      list = list.filter((u) => u.role === 'Student' && !u.teacherLabel);
    }
    if (noSubscriptionFilter) {
      list = list.filter(
        (u) => u.role === 'Student' && (u.subscriptionState || 'none') === 'none',
      );
    }
    if (newRegistrationsFilter) {
      list = list.filter((u) => isRegisteredWithinDays(u.createdAt, INACTIVE_DAYS));
    }
    if (hasMusicStyleFilter) {
      list = list.filter((u) =>
        studentMatchesMusicStyleFilters(u, musicStyleCoverage, musicStyleTags),
      );
    }
    const t = q.trim().toLowerCase();
    if (!t) return list;
    return list.filter((u) => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim().toLowerCase();
      return (
        (u.email || '').toLowerCase().includes(t) ||
        (u.nickname || '').toLowerCase().includes(t) ||
        name.includes(t) ||
        (u.role || '').toLowerCase().includes(t) ||
        (u.city || '').toLowerCase().includes(t) ||
        (u.teacherLabel || '').toLowerCase().includes(t)
      );
    });
  }, [
    q,
    users,
    roleFilter,
    statusFilter,
    teacherFilter,
    subscriptionFilter,
    activeFilter,
    inactiveFilter,
    noTeacherFilter,
    noSubscriptionFilter,
    newRegistrationsFilter,
    hasMusicStyleFilter,
    musicStyleCoverage,
    musicStyleTags,
  ]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === 'name') {
        return displayName(a).localeCompare(displayName(b)) * dir;
      }
      if (sortKey === 'registered') {
        return (
          (new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()) * dir
        );
      }
      if (sortKey === 'subscription') {
        return (a.subscriptionState || '').localeCompare(b.subscriptionState || '') * dir;
      }
      const at = a.lastConnected ? new Date(a.lastConnected).getTime() : 0;
      const bt = b.lastConnected ? new Date(b.lastConnected).getTime() : 0;
      return (at - bt) * dir;
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const scrollToUserList = useCallback(() => {
    window.requestAnimationFrame(() => {
      userListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const applyInsightFilter = useCallback(
    (insight: InsightFilter) => {
      const next = new URLSearchParams(searchParams);
      const isSelected = resolveSelectedInsight(searchParams) === insight;

      next.delete('activity');
      next.delete('inactive');
      next.delete('registered');
      next.delete('noTeacher');
      next.delete('noSubscription');

      if (!isSelected) {
        switch (insight) {
          case 'active':
            next.set('activity', 'active');
            break;
          case 'inactive':
            next.set('activity', 'inactive');
            break;
          case 'newRegistrations':
            next.set('registered', '30d');
            break;
          case 'noTeacher':
            next.set('noTeacher', '1');
            break;
          case 'noSubscription':
            next.set('noSubscription', '1');
            break;
          default:
            break;
        }
      }

      setSearchParams(next);
      scrollToUserList();
    },
    [searchParams, setSearchParams, scrollToUserList],
  );

  const clearInsightFilters = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('activity');
    next.delete('inactive');
    next.delete('registered');
    next.delete('noTeacher');
    next.delete('noSubscription');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const clearMusicStyleFilters = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('musicStyleCoverage');
    next.delete('musicStyles');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const applyMusicStyleCoverageFilter = useCallback(
    (coverage: MusicStyleCoverageFilter) => {
      const next = new URLSearchParams(searchParams);
      const isSelected = parseMusicStyleCoverage(searchParams) === coverage;

      if (isSelected) {
        next.delete('musicStyleCoverage');
      } else {
        next.set('musicStyleCoverage', coverage);
        if (coverage === 'none') {
          next.delete('musicStyles');
        }
      }

      setSearchParams(next);
      scrollToUserList();
    },
    [searchParams, setSearchParams, scrollToUserList],
  );

  const toggleMusicStyleTagFilter = useCallback(
    (tag: string) => {
      const next = new URLSearchParams(searchParams);
      const current = parseMusicStyleTags(searchParams);
      const isSelected = current.includes(tag);
      const updated = isSelected ? current.filter((t) => t !== tag) : [...current, tag];

      next.delete('musicStyleCoverage');
      if (updated.length > 0) {
        next.set('musicStyles', updated.join(','));
      } else {
        next.delete('musicStyles');
      }

      setSearchParams(next);
      scrollToUserList();
    },
    [searchParams, setSearchParams, scrollToUserList],
  );

  const hasListFilters =
    Boolean(roleFilter) ||
    Boolean(statusFilter) ||
    Boolean(teacherFilter) ||
    Boolean(subscriptionFilter) ||
    Boolean(selectedInsight) ||
    hasMusicStyleFilter;

  const canDeleteUser = (user: AdminUserRow) => (
    canDeleteUsers && user.role !== 'Admin' && user._id !== currentUserId
  );

  const openDelete = (event: React.MouseEvent, user: AdminUserRow) => {
    event.stopPropagation();
    setDeleteError(null);
    setDeleteTarget(user);
  };

  const handleConfirmDelete = async (confirmEmail: string) => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await deleteUserAndData(deleteTarget._id, confirmEmail);
      setUsers((current) => current.filter((row) => row._id !== deleteTarget._id));
      setDeleteTarget(null);
    } catch (e: any) {
      setDeleteError(e?.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <ContentPageLayout>
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={1}>
        <Box>
          <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 'bold', mb: 0 }}>
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Search users and open a customer profile (360° view).
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
          >
            <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="table"><TableRowsIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadCsv(getUsersExportUrl(), 'users-export.csv')}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {analytics ? (
        <UsersAnalyticsSection
          analytics={analytics}
          selectedInsight={selectedInsight}
          onInsightClick={applyInsightFilter}
          searchParams={searchParams}
          onMusicStyleCoverageClick={applyMusicStyleCoverageFilter}
          onMusicStyleTagClick={toggleMusicStyleTagFilter}
        />
      ) : null}

      <Paper ref={userListRef} sx={{ p: 2, mt: 1, scrollMarginTop: 16 }}>
        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ flex: '1 1 200px' }}
          />
          <TextField
            select
            size="small"
            label="Subscription"
            value={subscriptionFilter}
            onChange={(e) => updateFilter('subscription', e.target.value)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="trial">Trial</MenuItem>
            <MenuItem value="grace">Grace</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Role"
            value={roleFilter}
            onChange={(e) => updateFilter('role', e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Student">Student</MenuItem>
            <MenuItem value="Teacher">Teacher</MenuItem>
            <MenuItem value="Admin">Admin</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => updateFilter('status', e.target.value)}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Registered">Registered</MenuItem>
            <MenuItem value="Verified">Verified</MenuItem>
            <MenuItem value="New">New</MenuItem>
            <MenuItem value="Banned">Banned</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Activity"
            value={activeFilter ? 'active' : inactiveFilter ? 'inactive' : ''}
            onChange={(e) => {
              const next = new URLSearchParams(searchParams);
              next.delete('inactive');
              next.delete('activity');
              if (e.target.value) next.set('activity', e.target.value);
              setSearchParams(next);
            }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active 30d</MenuItem>
            <MenuItem value="inactive">Inactive 30d+</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Teacher"
            value={noTeacherFilter ? 'none' : noSubscriptionFilter ? 'noSub' : ''}
            onChange={(e) => {
              const next = new URLSearchParams(searchParams);
              next.delete('noTeacher');
              next.delete('noSubscription');
              if (e.target.value === 'none') next.set('noTeacher', '1');
              if (e.target.value === 'noSub') next.set('noSubscription', '1');
              setSearchParams(next);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="none">Student · no teacher</MenuItem>
            <MenuItem value="noSub">Student · no subscription</MenuItem>
          </TextField>
        </Box>

        {hasListFilters ? (
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
            <Typography variant="caption" color="primary">
              Showing {sorted.length} user{sorted.length === 1 ? '' : 's'}
              {selectedInsight === 'active' ? ' · active 30d' : ''}
              {selectedInsight === 'inactive' ? ' · inactive 30d+' : ''}
              {selectedInsight === 'newRegistrations' ? ' · registered in last 30 days' : ''}
              {selectedInsight === 'noTeacher' ? ' · students without teacher' : ''}
              {selectedInsight === 'noSubscription' ? ' · students without subscription' : ''}
              {musicStyleCoverage === 'with' ? ' · students with music styles' : ''}
              {musicStyleCoverage === 'none' ? ' · students with no styles set' : ''}
              {musicStyleTags.length > 0
                ? ` · style${musicStyleTags.length > 1 ? 's' : ''}: ${musicStyleTags.join(', ')}`
                : ''}
              {roleFilter ? ` · role=${roleFilter}` : ''}
              {statusFilter ? ` · status=${statusFilter}` : ''}
              {teacherFilter ? ` · teacher’s students` : ''}
              {subscriptionFilter ? ` · subscription=${subscriptionFilter}` : ''}
            </Typography>
            {selectedInsight ? (
              <Button size="small" onClick={clearInsightFilters}>
                Clear insight filter
              </Button>
            ) : null}
            {hasMusicStyleFilter ? (
              <Button size="small" onClick={clearMusicStyleFilters}>
                Clear style filter
              </Button>
            ) : null}
          </Box>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        ) : viewMode === 'table' ? (
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === 'subscription'}
                    direction={sortKey === 'subscription' ? sortDir : 'asc'}
                    onClick={() => handleSort('subscription')}
                  >
                    Subscription
                  </TableSortLabel>
                </TableCell>
                <TableCell>Teacher</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === 'lastActive'}
                    direction={sortKey === 'lastActive' ? sortDir : 'asc'}
                    onClick={() => handleSort('lastActive')}
                  >
                    Last active
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === 'registered'}
                    direction={sortKey === 'registered' ? sortDir : 'asc'}
                    onClick={() => handleSort('registered')}
                  >
                    Registered
                  </TableSortLabel>
                </TableCell>
                <TableCell>Risk</TableCell>
                {canDeleteUsers ? <TableCell align="right">Actions</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((u) => {
                const risk = deriveRisk(u);
                return (
                  <TableRow
                    key={u._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigateWithLang(`/users/${u._id}`)}
                  >
                    <TableCell>{displayName(u)}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                        <UserRoleBadges user={u} />
                      </Box>
                    </TableCell>
                    <TableCell>{u.subscriptionState || 'none'}</TableCell>
                    <TableCell>{u.teacherLabel || '—'}</TableCell>
                    <TableCell>
                      <UserActivityBadge lastConnected={u.lastConnected} inactiveDays={INACTIVE_DAYS} />
                    </TableCell>
                    <TableCell>{formatDate(u.lastConnected ?? undefined)}</TableCell>
                    <TableCell>{formatDate(u.createdAt)}</TableCell>
                    <TableCell>
                      {risk !== 'none' ? (
                        <OutlinedBadge
                          label={risk}
                          size="small"
                          color={risk === 'high' ? 'error' : 'warning'}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    {canDeleteUsers ? (
                      <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                        {canDeleteUser(u) ? (
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Delete ${displayName(u)}`}
                            onClick={(event) => openDelete(event, u)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <List sx={{ mt: 1 }}>
            {sorted.map((u, idx) => (
              <React.Fragment key={u._id}>
                <ListItemButton
                  onClick={() => navigateWithLang(`/users/${u._id}`)}
                  sx={{ py: 1.25, alignItems: 'flex-start' }}
                >
                  <ListItemAvatar sx={{ minWidth: 56, mt: 0.25 }}>
                    <UserListAvatar
                      avatarUrl={u.avatarUrl}
                      firstName={u.firstName}
                      lastName={u.lastName}
                      email={u.email}
                      nickname={u.nickname}
                      size={48}
                      isAdmin={u.role === 'Admin'}
                      isTeacher={userActsAsTeacher(u) && u.role !== 'Admin'}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    disableTypography
                    primary={
                      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={0.5}>
                        <Typography variant="subtitle1" fontWeight={600} component="span">
                          {displayName(u)}
                        </Typography>
                        {canDeleteUser(u) ? (
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Delete ${displayName(u)}`}
                            onClick={(event) => openDelete(event, u)}
                            sx={{ ml: 'auto' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                        {u.nickname && displayName(u) !== u.nickname ? (
                          <Typography variant="body2" color="text.secondary" component="span">
                            ({u.nickname})
                          </Typography>
                        ) : null}
                        <UserRoleBadges user={u} />
                        {u.status ? <UserStatusBadge status={u.status} /> : null}
                        <UserActivityBadge lastConnected={u.lastConnected} inactiveDays={INACTIVE_DAYS} />
                        {u.subscriptionState && u.subscriptionState !== 'none' ? (
                          <OutlinedBadge
                            label={u.subscriptionState}
                            size="small"
                            color={subscriptionChipColor(u.subscriptionState)}
                          />
                        ) : null}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" component="span" display="block">
                        Joined {formatDate(u.createdAt)}
                        {' · '}
                        Last active {formatDate(u.lastConnected ?? undefined)}
                        {u.teacherLabel ? ` · Teacher: ${u.teacherLabel}` : ''}
                        {u.city ? ` · ${u.city}` : ''}
                      </Typography>
                    }
                  />
                </ListItemButton>
                {idx < sorted.length - 1 ? <Divider component="li" /> : null}
              </React.Fragment>
            ))}
            {sorted.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, py: 2, textAlign: 'center' }}>
                No users found.
              </Typography>
            ) : null}
          </List>
        )}
      </Paper>
      <ConfirmDeleteUserDialog
        open={Boolean(deleteTarget)}
        email={deleteTarget?.email || ''}
        displayName={deleteTarget ? displayName(deleteTarget) : ''}
        loading={deleteLoading}
        error={deleteError}
        onClose={() => {
          if (!deleteLoading) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
        onConfirm={handleConfirmDelete}
      />
    </Box>
    </ContentPageLayout>
  );
};

export default UsersManagement;
